import MenuBookIcon from "@mui/icons-material/MenuBook";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useGetKnowledgeBaseLibrariesQuery } from "knowledge-base/_api/knowledge-base-api";
import { clientLibraryOf } from "knowledge-base/libraries/client-libraries";
import { clientLibraryPath } from "knowledge-base/knowledge-base-locations";
import { Link } from "react-router";

type Props = Readonly<{
  /**
   * The organization whose rules these are. Undefined is ordinary — a masked client team has no
   * legal entity id to offer — and renders nothing.
   */
  ownerLegalEntityId?: string;
}>;

/**
 * A way into a client's knowledge base from wherever their rules are already the subject.
 *
 * Reach to another organization's external library is granted by a hired work agreement, but until
 * now it was only *spendable* on the knowledge base page, inside a dropdown a reader had to already
 * know about. This is the same door, placed where someone looking up what a client requires is
 * actually standing.
 *
 * Renders nothing rather than a disabled control when the reader cannot open the library: an
 * agreement that has ended takes the rules with it, and a greyed-out link would advertise a
 * knowledge base to somebody who is no longer entitled to read it.
 */
export default function ClientKnowledgeBaseLink({ ownerLegalEntityId }: Props) {
  const { data: libraries } = useGetKnowledgeBaseLibrariesQuery();
  const library = clientLibraryOf(libraries, ownerLegalEntityId);

  if (!library) return null;

  return (
    <Stack spacing={0.5}>
      <Button
        component={Link}
        size="small"
        startIcon={<MenuBookIcon />}
        sx={{ alignSelf: "flex-start" }}
        to={clientLibraryPath(library.ownerLegalEntityId)}
      >
        {library.ownerName}&apos;s knowledge base
      </Button>
      <Typography color="text.secondary" variant="body2">
        Their knowledge base, which your agreement gives you access to.
      </Typography>
    </Stack>
  );
}
